import { useMemo } from 'react';
import type { ChatTheme } from '@kairelo/shared';

/**
 * Applies structured owner branding (concept §10).
 *
 * Every value is emitted as a CSS custom property after being validated by the
 * shared theme schema, so the only things that reach the stylesheet are a
 * `#rrggbb` colour or a value from a fixed enum. There is deliberately no path
 * that renders owner-supplied CSS: arbitrary custom CSS is out of V1.
 */

const FONT_STACKS: Record<ChatTheme['font'], string> = {
  system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  inter: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
  'ibm-plex-sans': "'IBM Plex Sans', -apple-system, 'Segoe UI', Arial, sans-serif",
  'ibm-plex-mono': "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  newsreader: "'Newsreader', Georgia, 'Times New Roman', serif",
  georgia: "Georgia, 'Times New Roman', serif",
};

const RADII: Record<ChatTheme['corners'], string> = {
  sharp: '2px',
  rounded: '12px',
  pill: '20px',
};

/** Style-level adjustments. Each is a fixed, reviewed set of values. */
const STYLE_DENSITY: Record<ChatTheme['style'], { scale: string; weight: string }> = {
  classic: { scale: '15px', weight: '400' },
  soft: { scale: '15.5px', weight: '400' },
  compact: { scale: '14px', weight: '400' },
  contrast: { scale: '15px', weight: '500' },
};

export function themeVariables(theme: ChatTheme): Record<string, string> {
  const density = STYLE_DENSITY[theme.style];
  return {
    // Remap the application accent as well, so buttons, badges and focus rings
    // inside a themed region adopt the owner's brand. Concept §10 says branding
    // applies to what *both* parties see; a visitor meeting an unbranded Join
    // button on an otherwise branded page is exactly the inconsistency that rule
    // exists to prevent.
    '--accent': theme.accentColour,
    '--accent-hover': theme.accentColour,
    '--focus': theme.accentColour,
    '--brand-accent': theme.accentColour,
    '--brand-background': theme.backgroundColour,
    '--brand-surface': theme.surfaceColour,
    '--brand-text': theme.textColour,
    '--brand-radius': RADII[theme.corners],
    '--brand-font': FONT_STACKS[theme.font],
    '--accent-ink': readableInk(theme.accentColour),
    fontSize: density.scale,
    fontWeight: density.weight,
  };
}

/** Picks black or white text for an arbitrary brand colour, by relative luminance. */
export function readableInk(hex: string): string {
  const value = hex.replace('#', '');
  const r = Number.parseInt(value.slice(0, 2), 16) / 255;
  const g = Number.parseInt(value.slice(2, 4), 16) / 255;
  const b = Number.parseInt(value.slice(4, 6), 16) / 255;
  const channel = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.45 ? '#14201e' : '#ffffff';
}

export function ThemedRegion({
  theme,
  className,
  children,
}: {
  theme: ChatTheme;
  className?: string;
  children: React.ReactNode;
}) {
  const style = useMemo(() => themeVariables(theme) as React.CSSProperties, [theme]);
  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
}
